import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Loader2, MessageSquare, Search } from 'lucide-react';
import { useState } from 'react';
import { useProjectContext } from '../../../../contexts/ProjectContext';
import { formatRelativeTime } from '../../../../utils/time';
import { useProjectConversations } from '../hooks/useProjectConversations';
import { useProposalV2Context } from '../ProposalV2Context';
/**
 * Full-page chat history for a proposal project.
 * Accessible via left nav "Chat History" item.
 * Clicking a chat opens it in the AI sidebar.
 */ export default function ChatHistoryPage() {
    const { projectId } = useProjectContext();
    const { setActiveChatId, setSidebarOpen } = useProposalV2Context();
    const [search, setSearch] = useState('');
    const { data: allChats, isLoading } = useProjectConversations(projectId);
    // Client-side search filter (server handles project scoping)
    const chats = (allChats ?? []).filter((c)=>!search || c.title.toLowerCase().includes(search.toLowerCase()));
    const handleSelectChat = (chatId)=>{
        setActiveChatId(chatId);
        setSidebarOpen(true);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "mx-auto max-w-3xl px-6 py-8",
        children: [
            /*#__PURE__*/ _jsx("h1", {
                className: "text-lg font-semibold text-slate-900",
                children: "Chat History"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "mt-1 text-sm text-slate-500",
                children: "View and resume past AI conversations for this proposal."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "relative mt-6",
                children: [
                    /*#__PURE__*/ _jsx(Search, {
                        className: "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        type: "text",
                        value: search,
                        onChange: (e)=>setSearch(e.target.value),
                        placeholder: "Search conversations...",
                        className: "w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-300 focus:outline-none focus:ring-1 focus:ring-violet-300"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-4",
                children: isLoading ? /*#__PURE__*/ _jsx("div", {
                    className: "flex items-center justify-center py-16",
                    children: /*#__PURE__*/ _jsx(Loader2, {
                        className: "h-6 w-6 animate-spin text-slate-400"
                    })
                }) : chats.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col items-center justify-center py-16 text-center",
                    children: [
                        /*#__PURE__*/ _jsx(MessageSquare, {
                            className: "h-8 w-8 text-slate-300"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mt-2 text-sm text-slate-500",
                            children: search ? 'No matching conversations' : 'No conversations yet'
                        })
                    ]
                }) : /*#__PURE__*/ _jsx("ul", {
                    className: "divide-y divide-slate-100 rounded-lg border border-slate-200",
                    children: chats.map((chat)=>/*#__PURE__*/ _jsx("li", {
                            children: /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>handleSelectChat(chat.chatId),
                                className: "flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors",
                                children: [
                                    /*#__PURE__*/ _jsx(MessageSquare, {
                                        className: "h-4 w-4 flex-shrink-0 text-slate-400"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "min-w-0 flex-1",
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: "truncate text-sm font-medium text-slate-800",
                                            children: chat.title
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "flex-shrink-0 text-xs text-slate-400",
                                        children: formatRelativeTime(chat.updatedAt || chat.createdAt)
                                    })
                                ]
                            })
                        }, chat.chatId))
                })
            })
        ]
    });
}
